/**
 * Processing Settings Component
 *
 * Configures processing mode (batch vs real-time) and provider settings
 * for feed import operations. Super admin only.
 *
 * Part of Story: Multi-Provider Processing Mode for Feed Imports
 * Date: 2026-03-06
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Settings,
  Zap,
  Clock,
  DollarSign,
  Info,
  Save,
  RefreshCw,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { authFetch } from '@/utils/auth-fetch';
import { useAuth } from '@/context/auth-context';

interface ProcessingConfig {
  mode: 'batch' | 'realtime';
  enrichment_provider: 'openai' | 'vertexai' | 'azure_openai';
  enrichment_model: string;
  batch_size: {
    embedding: number;
    enrichment: number;
  };
}

interface CostBucket {
  embedding: number | null;
  enrichment: number | null;
  benefit_scoring: number | null;
  total: number | null;
  pricingUnavailable?: boolean;
}

interface CostEstimate {
  batch: CostBucket;
  realtime: CostBucket;
  productCount: number;
}

// What the server accepts (ProcessingJobService VALID_MODES/VALID_PROVIDERS/VALID_MODELS), returned with every GET.
interface ValidValues {
  modes: string[];
  providers: string[];
  models: Record<string, string[]>;
}

interface ProcessingSettingsProps {
  siteId: string;
}

const PROVIDER_OPTIONS = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'vertexai', label: 'Google Vertex AI' },
  { value: 'azure_openai', label: 'Azure OpenAI', note: 'batch mode only' },
  // council-pm's 2026-08-28 ruling held azure_openai out of this list until the pipeline
  // supported it end to end (embedding + AEO generation, not just enrichment). Phase 2
  // (embedding, #5510) and Phase 3 (AEO generation, #5509) are now merged and confirmed live
  // in production, so the condition that ruling named is met — see task-qgd8r6unv8.
];

// Display text only. WHICH models can be picked comes from the server's validValues, never from this map: a
// hardcoded list here drifted from the backend (2026-10-14: gemini-3.8-flash accepted by the API but not offered here,
// gemini-2.5-pro / 1.5-pro offered here but rejected by the API). A model with no entry shows its id.
const MODEL_LABELS: Record<string, { label: string; description: string }> = {
  'gpt-4o-mini': { label: 'GPT-4o Mini', description: 'Fast & cost-effective' },
  'gpt-4o': { label: 'GPT-4o', description: 'Higher quality, higher cost' },
  'gemini-2.0-flash-lite': { label: 'Gemini 2.0 Flash-Lite', description: 'Lowest cost' },
  'gemini-2.0-flash': { label: 'Gemini 2.0 Flash', description: 'Fast & cost-effective' },
  'gemini-2.5-flash-lite': { label: 'Gemini 2.5 Flash-Lite', description: 'Low cost' },
  'gemini-2.5-flash': { label: 'Gemini 2.5 Flash', description: 'Premium quality' },
  'gemini-3.8-flash': { label: 'Gemini 3.8 Flash', description: 'Vertex batch, global route' },
  'gpt-4.1-mini-batch': {
    label: 'GPT-4.1 Mini (batch)',
    description: 'Only deployment provisioned on the Azure resource',
  },
};

function modelOptionsFor(validValues: ValidValues | null, provider: string) {
  return (validValues?.models?.[provider] || []).map(value => ({
    value,
    ...(MODEL_LABELS[value] || { label: value, description: '' }),
  }));
}

const DEFAULT_CONFIG: ProcessingConfig = {
  mode: 'batch',
  enrichment_provider: 'openai',
  enrichment_model: 'gpt-4o-mini',
  batch_size: {
    embedding: 500,
    enrichment: 100,
  },
};

// task-0ibq2r2liu (codex #5499 blocking, @81d5ca53c): estimateCost returns cost:null for an
// unpriced provider/model pair (azure_openai today) instead of a fabricated number — calling
// .toFixed() directly on that null is a client-side TypeError, not just a misleading render.
function formatCost(value: number | null): string {
  return value === null ? 'Pricing unavailable' : `$${value.toFixed(2)}`;
}

export function ProcessingSettings({ siteId }: ProcessingSettingsProps) {
  const [config, setConfig] = useState<ProcessingConfig>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [costEstimate, setCostEstimate] = useState<CostEstimate | null>(null);
  const [validValues, setValidValues] = useState<ValidValues | null>(null);
  // A failed load shows the error and NO form: the form's placeholder values must never be saved over the real config.
  const [loadError, setLoadError] = useState<string | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();

  const isSuperAdmin = user?.role === 'super_admin';

  // Load current configuration
  useEffect(() => {
    if (siteId) {
      loadConfig();
    }
  }, [siteId]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const response = await authFetch(`/sites/${siteId}/processing-config`);
      if (!response.ok) {
        const body = await response.text().catch(() => '');
        setLoadError(`The server answered HTTP ${response.status}${body ? `: ${body.slice(0, 300)}` : ''}`);
        return;
      }
      const data = await response.json();
      if (!data.config || !data.validValues || !data.validValues.models) {
        setLoadError('The server returned no processing config or no list of valid models');
        return;
      }
      setValidValues(data.validValues);
      setConfig({
        ...DEFAULT_CONFIG,
        ...data.config,
        batch_size: {
          ...DEFAULT_CONFIG.batch_size,
          ...(data.config.batch_size || {}),
        },
      });
    } catch (error) {
      console.error('Error loading processing config:', error);
      setLoadError(error instanceof Error ? error.message : 'Failed to load processing configuration');
      toast({
        title: 'Error',
        description: 'Failed to load processing configuration',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Estimate costs when config changes
  const estimateCosts = useCallback(async () => {
    if (!siteId) return;

    try {
      setEstimating(true);
      const response = await authFetch(`/sites/${siteId}/processing-config/estimate`, {
        method: 'POST',
        body: JSON.stringify({ config }),
      });

      if (response.ok) {
        const data = await response.json();
        setCostEstimate(data.estimate);
      }
    } catch (error) {
      console.error('Error estimating costs:', error);
    } finally {
      setEstimating(false);
    }
  }, [siteId, config]);

  useEffect(() => {
    if (!loading && siteId) {
      const timer = setTimeout(estimateCosts, 500);
      return () => clearTimeout(timer);
    }
  }, [config, loading, siteId, estimateCosts]);

  // Save configuration
  const handleSave = async () => {
    if (!isSuperAdmin) {
      toast({
        title: 'Access Denied',
        description: 'Only super admins can modify processing settings',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      const response = await authFetch(`/sites/${siteId}/processing-config`, {
        method: 'PUT',
        body: JSON.stringify({ config }),
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Processing settings updated successfully',
        });
        setHasChanges(false);
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save');
      }
    } catch (error) {
      console.error('Error saving processing config:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save processing settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle config changes
  const updateConfig = (updates: Partial<ProcessingConfig>) => {
    setConfig(prev => ({ ...prev, ...updates }));
    setHasChanges(true);
  };

  const updateBatchSize = (key: 'embedding' | 'enrichment', value: number) => {
    setConfig(prev => ({
      ...prev,
      batch_size: { ...prev.batch_size, [key]: value },
    }));
    setHasChanges(true);
  };

  // Handle provider change - reset model to default for new provider
  const handleProviderChange = (provider: 'openai' | 'vertexai' | 'azure_openai') => {
    const first = modelOptionsFor(validValues, provider)[0];
    if (!first) {
      toast({
        title: 'No models available',
        description: `The server lists no models for ${provider}; the provider was not changed.`,
        variant: 'destructive',
      });
      return;
    }
    const defaultModel = first.value;
    updateConfig({
      enrichment_provider: provider,
      enrichment_model: defaultModel,
      // azure_openai has no realtime path (ProcessingJobService rejects mode='realtime' with
      // enrichment_provider='azure_openai') — force batch mode on selection rather than let the
      // user land on an invalid combination the save call will reject.
      ...(provider === 'azure_openai' ? { mode: 'batch' as const } : {}),
    });
  };

  if (!isSuperAdmin) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Processing Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Processing settings are only accessible to super admins.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!loading && loadError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Processing Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Could not load this site&apos;s processing settings, so they are not shown and cannot be saved. {loadError}
            </AlertDescription>
          </Alert>
          <Button variant="outline" onClick={loadConfig}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const modelOptions = modelOptionsFor(validValues, config.enrichment_provider);
  const modelIsAccepted = modelOptions.some(option => option.value === config.enrichment_model);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-96" />
        </CardHeader>
        <CardContent className="space-y-6">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Processing Settings
            </CardTitle>
            <CardDescription>
              Configure how feed imports are processed. These settings affect enrichment, benefit scoring, and embedding generation.
            </CardDescription>
          </div>
          {hasChanges && (
            <Badge variant="secondary" className="animate-pulse">
              Unsaved changes
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-8">
        {/* Processing Mode */}
        <div className="space-y-4">
          <Label className="text-base font-semibold">Processing Mode</Label>
          <RadioGroup
            value={config.mode}
            onValueChange={(value: 'batch' | 'realtime') => updateConfig({ mode: value })}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div
                className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  config.mode === 'batch'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
                onClick={() => updateConfig({ mode: 'batch' })}
              >
                <div className="flex items-start gap-3">
                  <RadioGroupItem value="batch" id="batch" className="mt-1" />
                  <div className="flex-1">
                    <Label htmlFor="batch" className="cursor-pointer">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        <span className="font-semibold">Batch Processing</span>
                        <Badge variant="outline" className="text-xs">Recommended</Badge>
                      </div>
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      50% cost savings, 7-11 hours for large imports
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <DollarSign className="h-3 w-3 text-green-600" />
                        Lower cost
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Slower
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className={`relative p-4 rounded-lg border-2 transition-all ${
                  config.enrichment_provider === 'azure_openai'
                    ? 'opacity-50 cursor-not-allowed border-border'
                    : config.mode === 'realtime'
                      ? 'border-primary bg-primary/5 cursor-pointer'
                      : 'border-border hover:border-primary/50 cursor-pointer'
                }`}
                onClick={() => {
                  if (config.enrichment_provider !== 'azure_openai') {
                    updateConfig({ mode: 'realtime' });
                  }
                }}
              >
                <div className="flex items-start gap-3">
                  <RadioGroupItem
                    value="realtime"
                    id="realtime"
                    className="mt-1"
                    disabled={config.enrichment_provider === 'azure_openai'}
                  />
                  <div className="flex-1">
                    <Label htmlFor="realtime" className="cursor-pointer">
                      <div className="flex items-center gap-2">
                        <Zap className="h-4 w-4" />
                        <span className="font-semibold">Real-time Processing</span>
                      </div>
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      2x cost, ~1 hour for large imports (10x faster)
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <DollarSign className="h-3 w-3 text-amber-600" />
                        Higher cost
                      </span>
                      <span className="flex items-center gap-1">
                        <Zap className="h-3 w-3 text-green-600" />
                        Faster
                      </span>
                    </div>
                    {config.enrichment_provider === 'azure_openai' && (
                      <p className="text-xs text-amber-600 mt-2">
                        Not available for Azure OpenAI — batch mode only.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </RadioGroup>
        </div>

        {/* Provider & Model Selection */}
        <div className="space-y-4 border-t pt-6">
          <Label className="text-base font-semibold">Enrichment Provider & Model</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="provider">Provider</Label>
              <Select
                value={config.enrichment_provider}
                onValueChange={(value: 'openai' | 'vertexai' | 'azure_openai') => handleProviderChange(value)}
              >
                <SelectTrigger id="provider">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROVIDER_OPTIONS.filter(option => validValues?.providers?.includes(option.value)).map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      <div>
                        <div className="font-medium">{option.label}</div>
                        <div className="text-xs text-muted-foreground">
                          {[modelOptionsFor(validValues, option.value).map(m => m.label).join(', '), option.note]
                            .filter(Boolean).join(' — ')}
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="model">Model</Label>
              <Select
                value={config.enrichment_model}
                onValueChange={(value) => updateConfig({ enrichment_model: value })}
              >
                <SelectTrigger id="model">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {modelOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      <div>
                        <div className="font-medium">{option.label}</div>
                        {option.description && <div className="text-xs text-muted-foreground">{option.description}</div>}
                      </div>
                    </SelectItem>
                  ))}
                  {/* The stored model is shown as it is, even when the server no longer accepts it: a blank picker
                      would hide what the site is actually configured with. */}
                  {!modelIsAccepted && (
                    <SelectItem value={config.enrichment_model}>
                      <div className="font-medium">{config.enrichment_model} (not accepted by the server)</div>
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          {!modelIsAccepted && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                This site is configured with <strong>{config.enrichment_model}</strong>, which the server does not accept
                for {config.enrichment_provider}. Saving will be refused until a listed model is chosen.
              </AlertDescription>
            </Alert>
          )}
          {config.enrichment_provider === 'azure_openai' && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                <strong>All three batch phases are live.</strong> Enrichment and AEO page generation
                use this deployment (gpt-4.1-mini-batch). Embedding uses a separately configured
                Azure deployment, not this one — see Embeddings below. One open question, not a
                functional gap: AEO output quality from gpt-4.1-mini-batch hasn't been spot-checked
                against gpt-4o-mini for our templates yet — worth a quality check on the first pages
                generated from Azure-enriched attributes.
              </AlertDescription>
            </Alert>
          )}
        </div>

        {/* Embeddings Info */}
        <div className="space-y-4 border-t pt-6">
          <Label className="text-base font-semibold">Embeddings</Label>
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              {config.enrichment_provider === 'azure_openai' ? (
                <>
                  <strong>Model:</strong> a separately configured Azure deployment (not
                  gpt-4.1-mini-batch above) — the resource's operator sets which deployment via
                  server config; this is not adjustable from this screen.
                  <br />
                  <span className="text-muted-foreground">
                    Vector dimensions must match every other site's embeddings for search to stay
                    consistent — confirm the configured deployment produces compatible vectors
                    before relying on it for a live catalog.
                  </span>
                </>
              ) : (
                <>
                  <strong>Model:</strong> text-embedding-3-large (OpenAI)
                  <br />
                  <span className="text-muted-foreground">
                    Embedding model is system-wide and cannot be changed per-site to ensure consistent vector search.
                  </span>
                </>
              )}
            </AlertDescription>
          </Alert>
        </div>

        {/* Batch Size Configuration (only for batch mode) */}
        {config.mode === 'batch' && (
          <div className="space-y-6 border-t pt-6">
            <Label className="text-base font-semibold">Batch Sizes</Label>
            <p className="text-sm text-muted-foreground">
              Larger batches reduce API overhead. DB operations remain chunked at 50 records for safety.
            </p>

            <div className="space-y-6">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label htmlFor="embedding-batch">Embedding batch size</Label>
                  <span className="text-sm font-mono bg-muted px-2 py-1 rounded">
                    {config.batch_size.embedding.toLocaleString()} records
                  </span>
                </div>
                <Slider
                  id="embedding-batch"
                  value={[config.batch_size.embedding]}
                  onValueChange={([value]) => updateBatchSize('embedding', value)}
                  min={50}
                  max={5000}
                  step={50}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>50</span>
                  <span>5,000</span>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label htmlFor="enrichment-batch">Enrichment batch size</Label>
                  <span className="text-sm font-mono bg-muted px-2 py-1 rounded">
                    {config.batch_size.enrichment.toLocaleString()} records
                  </span>
                </div>
                <Slider
                  id="enrichment-batch"
                  value={[config.batch_size.enrichment]}
                  onValueChange={([value]) => updateBatchSize('enrichment', value)}
                  min={50}
                  max={2000}
                  step={50}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>50</span>
                  <span>2,000</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cost Estimate */}
        {costEstimate && (
          <div className="space-y-4 border-t pt-6">
            <div className="flex items-center gap-2">
              <Label className="text-base font-semibold">Cost Estimate</Label>
              {estimating && <Loader2 className="h-4 w-4 animate-spin" />}
            </div>
            <p className="text-sm text-muted-foreground">
              Estimated costs for {costEstimate.productCount.toLocaleString()} products
            </p>

            <div className="grid grid-cols-2 gap-4">
              <div className={`p-4 rounded-lg border ${config.mode === 'batch' ? 'border-primary bg-primary/5' : 'border-border'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="h-4 w-4" />
                  <span className="font-medium">Batch Mode</span>
                  {config.mode === 'batch' && <Badge variant="secondary" className="text-xs">Selected</Badge>}
                </div>
                <div className="text-2xl font-bold text-green-600">
                  {formatCost(costEstimate.batch.total)}
                </div>
                <div className="text-xs text-muted-foreground mt-1 space-y-1">
                  <div>Embedding: {formatCost(costEstimate.batch.embedding)}</div>
                  <div>Enrichment: {formatCost(costEstimate.batch.enrichment)}</div>
                  <div>Benefit scoring: {formatCost(costEstimate.batch.benefit_scoring)}</div>
                  {costEstimate.batch.pricingUnavailable && (
                    <div className="text-amber-600">Some phases have no pricing data yet — total excludes them, not $0.</div>
                  )}
                </div>
              </div>

              <div className={`p-4 rounded-lg border ${config.mode === 'realtime' ? 'border-primary bg-primary/5' : 'border-border'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="h-4 w-4" />
                  <span className="font-medium">Real-time Mode</span>
                  {config.mode === 'realtime' && <Badge variant="secondary" className="text-xs">Selected</Badge>}
                </div>
                <div className="text-2xl font-bold text-amber-600">
                  {formatCost(costEstimate.realtime.total)}
                </div>
                <div className="text-xs text-muted-foreground mt-1 space-y-1">
                  <div>Embedding: {formatCost(costEstimate.realtime.embedding)}</div>
                  <div>Enrichment: {formatCost(costEstimate.realtime.enrichment)}</div>
                  <div>Benefit scoring: {formatCost(costEstimate.realtime.benefit_scoring)}</div>
                  {costEstimate.realtime.pricingUnavailable && (
                    <div className="text-amber-600">Some phases have no pricing data yet — total excludes them, not $0.</div>
                  )}
                </div>
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              * Estimates based on average token usage. Actual costs may vary.
            </p>
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end pt-4 border-t">
          <Button onClick={handleSave} disabled={saving || !hasChanges}>
            {saving ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Processing Settings
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
